<template>
    <div
        class="gh-marquee"
        :class="{
            'gh-marquee--pause-on-hover': pauseOnHover,
            'gh-marquee--disabled': disabled,
        }"
        :style="marqueeStyle"
    >
        <div class="gh-marquee__track">
            <div class="gh-marquee__content">
                <slot></slot>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, type CSSProperties } from 'vue';

    type MarqueeStyle = CSSProperties & {
        '--gh-marquee-duration': string;
    };

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            duration?: number;
            pauseOnHover?: boolean;
            disabled?: boolean;
        }>(),
        {
            duration: 20,
            pauseOnHover: true,
            disabled: false,
        }
    );

    // ----- computed -----
    const normalizedDuration = computed(() => {
        const duration = Number(props.duration);
        return Number.isFinite(duration) ? Math.max(duration, 0.1) : 20;
    });

    const marqueeStyle = computed<MarqueeStyle>(() => {
        return {
            '--gh-marquee-duration': `${normalizedDuration.value}s`,
        };
    });
</script>

<style scoped lang="scss">
    .gh-marquee {
        display: block;
        width: 100%;
        overflow: hidden;

        &__track {
            display: flex;
            width: max-content;
            padding-left: 100%;
            animation: gh-marquee-scroll var(--gh-marquee-duration) linear
                infinite;
            will-change: transform;
        }

        &__content {
            flex: 0 0 auto;
            white-space: nowrap;
        }

        &--pause-on-hover:hover &__track,
        &--pause-on-hover:focus-within &__track {
            animation-play-state: paused;
        }

        &--disabled &__track {
            display: block;
            width: 100%;
            padding-left: 0;
            animation: none;
            transform: none;
        }

        &--disabled &__content {
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }

    @keyframes gh-marquee-scroll {
        from {
            transform: translateX(0);
        }

        to {
            transform: translateX(-100%);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .gh-marquee {
            &__track {
                display: block;
                width: 100%;
                padding-left: 0;
                animation: none;
                transform: none;
            }

            &__content {
                white-space: normal;
            }
        }
    }
</style>
